import algoliasearch from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import { searchBox, hits, configure } from 'instantsearch.js/es/widgets';
import history from 'instantsearch.js/es/lib/routers/history';
import debounce from 'lodash.debounce';

function googleAnalyticsMiddleware() {
    const sendEventDebounced = debounce(() => {
        // Check if gtag is defined
        if (typeof gtag !== 'function') return;

        gtag('event', 'page_view', {
            page_location: window.location.pathname + window.location.search,
        });
    }, 3000);

    return {
        onStateChange() {
            sendEventDebounced();
        },
        subscribe() {},
        unsubscribe() {},
    };
}

const searchClient = algoliasearch(
    window.themeData.algolia_app_id,
    window.themeData.algolia_search_key
);

const routingSettings = {
    router: history(),
    stateMapping: {
        stateToRoute(uiState) {
            const indexUiState = uiState[window.themeData.algolia_index_name];
            return {
                q: indexUiState.query,
            };
        },
        routeToState(routeState) {
            return {
                [window.themeData.algolia_index_name]: {
                    query: routeState.q,
                },
            };
        },
    },
};

const search = instantsearch({
    indexName: window.themeData.algolia_index_name,
    searchClient,
    searchFunction(helper) {
        const container = document.querySelector('#search-hits');
        if (helper.state.query === '') container.classList.add('empty');
        else container.classList.remove('empty');

        if (helper.state.query) {
            helper.search();
        }
    },
    routing: routingSettings,
});

search.use(googleAnalyticsMiddleware);

class Algolia {
    constructor() {
        this.searchContainer = document.querySelector('#search-input');
        this.hitsContainer = document.querySelector('#search-hits');

        if (!this.searchContainer || !this.hitsContainer) return;

        this.init();
    }

    init() {
        search.addWidgets([
            searchBox({
                container: this.searchContainer,
                placeholder: 'Search',
                showSubmit: false,
                showReset: false,
                showLoadingIndicator: false,
                cssClasses: {
                    root: 'search-input',
                    form: 'search-input__form',
                    input: 'search-input__input',
                    reset: 'search-input__reset',
                    resetIcon: 'search-input__resetIcon',
                    loadingIndicator: 'search-input__loadingIndicator',
                    loadingIcon: 'search-inputIcon',
                    submit: 'search-input__submit',
                    submitIcon: 'search-input__submitIcon',
                },
            }),
            configure({
                hitsPerPage: 40,
            }),
            hits({
                container: this.hitsContainer,
                cssClasses: {
                    root: 'search-hits',
                    emptyRoot: 'search-hits--empty',
                    list: 'search-hits__list',
                    item: 'search-hits__item',
                },
                templates: {
                    item(hit, { html, components }) {
                        const subtitle = [];
                        if (hit.type === 'post') subtitle.push('News');
                        if (hit.type === 'area') subtitle.push('Expertise');
                        if (hit.type === 'person') subtitle.push('Staff');
                        if (hit.type === 'page') subtitle.push('Page');
                        if (hit.type === 'event') subtitle.push('Event');
                        if (hit.type === 'barrister')
                            subtitle.push('Barrister');

                        if (hit.subtitle) subtitle.push(hit.subtitle);

                        return html`
                            <a
                                class="search-hit"
                                title="${hit.title}"
                                href="${hit.permalink}"
                            >
                                ${hit.image
                                    ? html`<div class="search-hit__img">
                                          <img
                                              src="${hit.image}"
                                              alt="${hit.title}"
                                          />
                                      </div>`
                                    : ''}
                                <div class="search-hit__content">
                                    <div class="search-hit__subtitle">
                                        ${subtitle.join(' • ')}
                                    </div>
                                    <div class="search-hit__title">
                                        ${components.Highlight({
                                            hit,
                                            attribute: 'title',
                                        })}
                                    </div>

                                    ${hit.content && hit.content.length > 0
                                        ? html`<div class="search-hit__text">
                                              ${components.Snippet({
                                                  hit,
                                                  attribute: 'content',
                                              })}
                                          </div>`
                                        : ''}
                                </div>
                                <div class="search-hit__arrow">
                                    <svg
                                        width="40"
                                        height="40"
                                        viewBox="0 0 40 40"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M22.5208 28.7917C22.1874 28.4583 22.0274 28.0556 22.0408 27.5833C22.0552 27.1111 22.2291 26.7083 22.5624 26.375L27.2708 21.6667H8.68742C8.2152 21.6667 7.81909 21.5067 7.49909 21.1867C7.1802 20.8678 7.02075 20.4722 7.02075 20C7.02075 19.5278 7.1802 19.1317 7.49909 18.8117C7.81909 18.4928 8.2152 18.3333 8.68742 18.3333H27.2708L22.5208 13.5833C22.1874 13.25 22.0208 12.8539 22.0208 12.395C22.0208 11.9372 22.1874 11.5417 22.5208 11.2083C22.8541 10.875 23.2502 10.7083 23.7091 10.7083C24.1669 10.7083 24.5624 10.875 24.8958 11.2083L32.5208 18.8333C32.6874 19 32.8058 19.1806 32.8758 19.375C32.9446 19.5695 32.9791 19.7778 32.9791 20C32.9791 20.2222 32.9446 20.4306 32.8758 20.625C32.8058 20.8195 32.6874 21 32.5208 21.1667L24.8541 28.8333C24.5485 29.1389 24.1669 29.2917 23.7091 29.2917C23.2502 29.2917 22.8541 29.125 22.5208 28.7917Z"
                                            fill="black"
                                        />
                                    </svg>
                                </div>
                            </a>
                        `;
                    },

                    empty(results, { html }) {
                        if (!results.query) {
                            return html`<p>Start to search</q></p>`;
                        } else {
                            return html`<p>
                                No results for <q>${results.query}</q>
                            </p>`;
                        }
                    },
                },
            }),
        ]);

        search.start();
    }
}

export default new Algolia();
